import { useState } from "react";
import { Button } from "@app/components/ui/button";
import { Label } from "@app/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";

interface ExampleEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    inputs: Record<string, unknown>;
    expected_outputs: Record<string, unknown> | null;
  }) => void;
  initialInputs?: Record<string, unknown>;
  initialExpectedOutputs?: Record<string, unknown> | null;
  title?: string;
  hideInputs?: boolean;
}

export function ExampleEditor({
  open,
  onClose,
  onSave,
  initialInputs,
  initialExpectedOutputs,
  title = "Ajouter un exemple",
  hideInputs = false,
}: ExampleEditorProps) {
  const [inputsText, setInputsText] = useState(
    initialInputs
      ? JSON.stringify(initialInputs, null, 2)
      : '{\n  "input": ""\n}',
  );
  const [expectedText, setExpectedText] = useState(
    initialExpectedOutputs
      ? JSON.stringify(initialExpectedOutputs, null, 2)
      : '{\n  "output": ""\n}',
  );
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);
    try {
      const inputs = hideInputs
        ? (initialInputs ?? {})
        : JSON.parse(inputsText);
      let expected = null;
      if (expectedText.trim()) {
        expected = JSON.parse(expectedText);
      }
      onSave({ inputs, expected_outputs: expected });
      onClose();
    } catch {
      setError("JSON invalide. Vérifiez la syntaxe.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!hideInputs && (
            <div>
              <Label className="text-sm font-semibold">Entrées (inputs) *</Label>
              <textarea
                className="mt-1 w-full h-40 font-mono text-xs border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={inputsText}
                onChange={(e) => setInputsText(e.target.value)}
                placeholder='{"input": "valeur"}'
              />
            </div>
          )}

          <div>
            <Label className="text-sm font-semibold">
              Sorties attendues (expected_outputs)
            </Label>
            <textarea
              className="mt-1 w-full h-40 font-mono text-xs border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={expectedText}
              onChange={(e) => setExpectedText(e.target.value)}
              placeholder='{"output": "valeur attendue"}'
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSave}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
